import OrganizationRepository from "@/js/api/OrganizationRepository"
import TClassRepository from "@/js/api/private/TClassRepository"
import TEventRepository from "@/js/api/private/TEventRepository"
import TOrganizationRepository from "@/js/api/private/TOrganizationRepository"
import commonUtil from "@/js/utils/commonUtil"

/** Account type constants – use these instead of string literals elsewhere (user store, PortalAccountMenu, etc.). */
export const ACCOUNT_TYPE_ORGANIZATION = 'organization'
export const ACCOUNT_TYPE_PARTICIPANT = 'participant'

const getDefaultState = () => {
  return {
    activeAccount: null, //This is full account object
    activeOrganization: null, //This is full organization object for active account
    myClasses: null,
    myEvents: null
  }
}

export default {
  namespaced: true,
  state: getDefaultState(),

  mutations: {
    resetState (state) {
      Object.assign(state, getDefaultState())
    },
    setClasses (state, myClasses) {
      state.myClasses = myClasses
    },
    updateClass (state, updatedClass) {
      if (!updatedClass) {
        return
      }

      updatedClass = commonUtil.clone(updatedClass)
      const item = state.myClasses.find(item => item.id === updatedClass.id)
      Object.assign(item, updatedClass)
    },
    addClass (state, newClass) {
      if (!newClass) {
        return
      }

      state.myClasses.push(newClass)
    },
    setMyEvents (state, myEvents) {
      state.myEvents = myEvents
    },
    addEvents (state, myEvents) {
      if (!myEvents) {
        return
      }
      if (!state.myEvents) {
        Object.assign(state.myEvents, myEvents)
      } else {
        state.myEvents = state.myEvents.concat(myEvents)
      }
    },
    updateEvent (state, updatedEvent) {
      if (!updatedEvent) {
        return
      }

      updatedEvent = commonUtil.clone(updatedEvent)
      const item = state.myEvents.find(item => item.id === updatedEvent.id)
      Object.assign(item, updatedEvent)
    },
    setActiveOrganization (state, organization) {
      state.activeOrganization = organization
    },
    setAccount (state, payload) {
      if (!payload) return
      state.activeAccount = payload
      state.activeOrganization = null
      state.myClasses = null
      state.myEvents = null
    }
  },

  actions: {
    resetState ({ commit }) {
      commit('resetState')
    },

    async loadMyClasses ({ commit, getters }, isForce) {
      if (!isForce){
        let myClasses = getters.myClasses
        if (myClasses){
          return
        }
      }

      await TClassRepository.getAll()
        .then(myClasses => {
          commit('setClasses', myClasses)
        })
        .catch(error => {
          throw error;
        })
    },
    async loadMyEvents({ commit, getters }, isForce){
      if (!isForce){
        let myEvents = getters.myEvents
        if (myEvents){
          return
        }
      }

      await TEventRepository.getMyEvents(Date.now(), null)
        .then(myEvents => {
          commit('setMyEvents', myEvents)
        })
        .catch(error => {
          throw error
        })
    },

    updateClass({ commit }, myClass){
      commit('updateClass', myClass)
    },
    addClass({ commit }, newClass){
      commit('addClass', newClass)
    },
    setMyEvents ({ commit }, myEvents){
      commit('setMyEvents', myEvents)
    },
    addEvents ({ commit }, newEvents){
      commit('addEvents', newEvents)
    },
    updateEvent({ commit }, myEvent){
      commit('updateEvent', myEvent)
    },
    async setAccount ({ commit }, payload) {
      if (!payload) return
      commit('setAccount', payload)
      
      if (payload.type === ACCOUNT_TYPE_ORGANIZATION && payload.organizationId) {
        await TOrganizationRepository.get(payload.organizationId)
          .then(organization => commit('setActiveOrganization', organization))
          .catch(err => {
            console.warn('Failed to fetch organization:', payload.organizationId, err)
            commit('setActiveOrganization', null)
          })
      } else {
        commit('setActiveOrganization', null)
      }
    },
    async reloadActiveOrganization ({ commit, getters }) {
      const organizationId = getters.activeOrganizationId
      if (!organizationId) {
        console.error('Organization ID is not set')
        return
      }
      await TOrganizationRepository.get(organizationId)
        .then(organization => commit('setActiveOrganization', organization))
        .catch(error => { throw error })
    },
    /**
     * Disconnect Stripe Connect for the active organization (backend clears stripe_connect_account_id and module_online_payments_enabled).
     * Backend returns the updated organization; we update the store with it.
     * @returns {Promise<Object>} updated organization
     */
    async stripeConnectDisconnect ({ commit, getters }) {
      const organizationId = getters.activeOrganizationId
      if (!organizationId) {
        throw new Error('Organization ID is not set')
      }
      const updated = await TOrganizationRepository.stripeConnectDisconnect()
      commit('setActiveOrganization', updated)
      return updated
    },
    updateActiveOrganization ({ commit }, organization) {
      commit('setActiveOrganization', organization)
    },
    /**
     * Patch organization: sends payload to API (with is_patch), then updates store with returned org.
     * @param {Object} context - { commit, getters }
     * @param {Object} patchPayload - partial org fields to patch (e.g. { membership_settings: {...}, organization_settings: {...} })
     * @returns {Promise<Object>} - updated organization
     */
    async patchActiveOrganization ({ commit, getters }, patchPayload) {
      const organizationId = getters.activeOrganizationId
      if (!organizationId) {
        throw new Error('Organization ID is not set')
      }
      const updated = await TOrganizationRepository.updateOrganization(patchPayload)
      commit('setActiveOrganization', updated)
      return updated
    },
    async setAccountByIdentifier({ rootGetters }, identifier) {
      const accounts = rootGetters['user/accountsForSwitcher']
      if (!Array.isArray(accounts))
        return

      const want = (identifier || '').replace(/_/g, '-').toLowerCase().trim()
      const account = accounts.find(acc => {
        const id = (acc.organizationIdentifier ?? acc.identifier ?? '').replace(/_/g, '-').toLowerCase().trim()
        return id === want
      })
      if (account) {
        await this.dispatch('dashboard/setAccount', account)
      } else {
        //Else let's set up default account
        const account = accounts[0]
        if (account) {
          await this.dispatch('dashboard/setAccount', account)
        }
      }
    },
    async setDefaultAccount({ rootGetters }) {
      const accounts = rootGetters['user/accountsForSwitcher']
      if (!Array.isArray(accounts)) return
      const account = accounts[0]
      if (account) {
        await this.dispatch('dashboard/setAccount', account)
      }
    },
  },
  getters: {
    isAccountTypeOrganization: state => state.activeAccount?.type === ACCOUNT_TYPE_ORGANIZATION,
    isAccountTypeParticipant: state => state.activeAccount?.type === ACCOUNT_TYPE_PARTICIPANT,
    activeOrganization: state => state.activeOrganization,
    activeOrganizationId: state => state.activeOrganization ? state.activeOrganization.id : null,
    /** Current account for switcher/layout: { type, orgId, displayName, roles, organizationIdentifier, organizations? } */
    currentAccount: state => state.activeAccount,
    currentAccountUrl: state => state.activeAccount?.account_url,
    currentAccountName: state => state.activeAccount?.displayName,
    currentAccountIdentifier: state => state.activeAccount?.organizationIdentifier,
    currentAccountRoles: state => state.activeAccount?.roles,
    currentAccountOrganizations: state => state.activeAccount?.organizations,
    participantOrganizations: state => state.activeAccount?.organizations || [],
    currentAccountLogoUrl: state => state.activeAccount?.logo_url,
    currentAccountSrcLogoLarge: state => state.activeAccount?.src_logo_large,
    //Fix this
    isOrganizationStudio: state => state.activeOrganization && state.activeOrganization.organization_type === OrganizationRepository.ORGANIZATION_TYPE_ORGANIZATION,
    isOrganizationIndividual: state => state.activeOrganization && state.activeOrganization.organization_type === OrganizationRepository.ORGANIZATION_TYPE_INDIVIDUAL,
    isCardView: state => state.activeOrganization && state.activeOrganization.layout_style === OrganizationRepository.LAYOUT_STYLE_CARD,
    isWeekView: state => state.activeOrganization && state.activeOrganization.layout_style === OrganizationRepository.LAYOUT_STYLE_WEEK,
    myClasses: state => state.myClasses,
    myEvents: state => state.myEvents,
    eventParticipantTags: state => {
      const org = state.activeOrganization
      if (!org || !org.mypages_settings) return []
      const mypagesSettings = org.mypages_settings
      if (mypagesSettings.event_participant_tags && Array.isArray(mypagesSettings.event_participant_tags)) {
        return mypagesSettings.event_participant_tags
      }
      return []
    },
    organizationParticipantTags: state => {
      const org = state.activeOrganization
      if (!org || !org.mypages_settings) return []
      const mypagesSettings = org.mypages_settings
      if (mypagesSettings.organization_participant_tags && Array.isArray(mypagesSettings.organization_participant_tags)) {
        return mypagesSettings.organization_participant_tags
      }
      return []
    },
    eventById: state => (id) => {
      if (!id) {
        return null
      }

      id = parseInt(id)
      const myEventList = state.myEvents.filter(myEvent => myEvent.id === id)
      if (myEventList) {
        return myEventList[0]
      } else {
        return null
      }
    },
    classById: state => (id) => {
      if (!id) {
        return null
      }

      id = parseInt(id)
      const myClassesList = state.myClasses.filter(myClass => myClass.id === id)
      if (myClassesList) {
        return myClassesList[0]
      } else {
        return null
      }
    },
  }
}
